import { RefObject, useCallback, useEffect, useMemo, useState } from 'react';
import { VirtuosoHandle } from 'react-virtuoso';

import { TraceLogInfoLevel } from '@/Pages/Graph/src/control/types';

import { useTraceLogExpansionStateForPath } from '../store/traceLogStore';

export const useHandleNodeOrTurnExpansion = ({
  virtuosoRef,
  expansionPath,
  forceExpanded,
  expansionLevel,
}: {
  virtuosoRef: RefObject<VirtuosoHandle>;
  expansionPath: string[];
  forceExpanded?: boolean;
  expansionLevel: TraceLogInfoLevel;
}) => {
  const { getIsExpanded, setIsExpanded } =
    useTraceLogExpansionStateForPath(expansionLevel);
  const isExpanded = useMemo(
    () => getIsExpanded(expansionPath, forceExpanded),
    [getIsExpanded, expansionPath, forceExpanded]
  );

  const [needsScroll, setNeedsScroll] = useState<number | undefined>(undefined);

  // during expansion/collapse of node capture the scroll position
  const setExpanded = useCallback(
    (path: string[], open: boolean) => {
      virtuosoRef.current?.getState((s) => {
        const scrollTop = s.scrollTop;
        setIsExpanded(path, open);
        setNeedsScroll(scrollTop);
      });
    },
    [setIsExpanded, virtuosoRef]
  );

  // and restore it after the expansion/collapse is complete
  useEffect(() => {
    if (needsScroll !== undefined) {
      setTimeout(() => {
        virtuosoRef.current?.scrollTo({
          top: needsScroll,
          behavior: 'auto',
        });
        setNeedsScroll(undefined);
      }, 100);
    }
  }, [needsScroll, virtuosoRef]);

  return {
    isExpanded,
    setExpanded,
  };
};
